import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import { apiError } from '../format';
import { rulesApi } from './rulesApi';
import {
  BORDER, DARK, FIELD_SX, FLOAT_SHADOW, GHOST_BTN_SX, KIND_LABELS, KIND_ORDER, MODE_HINTS, MODE_LABELS, MUTED, PERIOD_LABELS,
  PERM_HINTS_RULES, PRIMARY, PRIMARY_BTN_SX, PRIMARY_SOFT, SOFT, minValue, valueError
} from './rulesConstants';
import { ModeChip, Panel, PanelHeader, Segmented, modeOptions } from './RuleBits';
import RuleMatrix from './RuleMatrix';
import { DocKind, NetworkItem, RuleDef, RuleMode, RulePeriod, RuleRecord, RulesOverview } from './rulesTypes';
import { notify } from '../../../notifications';

interface Props {
  overview: RulesOverview;
  onSaved: () => void;
}

interface Cell { mode: RuleMode; value: string; period: RulePeriod }

const keyOf = (rule: string, kind: DocKind) => `${rule}|${kind}`;

const baseCell = (def: RuleDef, rec?: RuleRecord): Cell => ({
  mode: rec?.mode ?? 'off',
  value: String(rec ? rec.value : def.defaultValue),
  period: rec?.period ?? 'day'
});

const cellError = (def: RuleDef, c: Cell) => valueError(def, c.mode, c.value);

type View = 'matrix' | 'list';
const VIEW_STORAGE_KEY = 'zerde.docRules.view';

/** Запомненный вид вкладки (только удобство конкретного браузера) */
const readView = (): View => {
  try { return localStorage.getItem(VIEW_STORAGE_KEY) === 'list' ? 'list' : 'matrix'; } catch { return 'matrix'; }
};

/** Вкладка «Правила»: значения для всей сети по каждому виду документа */
const NetworkRulesTab: React.FC<Props> = ({ overview, onSaved }) => {
  const { catalog, network, canEditNetwork } = overview;

  const base = useMemo(() => {
    const map: Record<string, Cell> = {};
    catalog.rules.forEach(def => KIND_ORDER.forEach(kind => {
      map[keyOf(def.code, kind)] = baseCell(def, network.find(r => r.rule_code === def.code && r.doc_kind === kind));
    }));
    return map;
  }, [catalog.rules, network]);

  const [draft, setDraft] = useState<Record<string, Cell>>(base);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<View>(readView);

  useEffect(() => { setDraft(base); }, [base]);

  const changeView = (v: View) => {
    setView(v);
    try { localStorage.setItem(VIEW_STORAGE_KEY, v); } catch { /* хранилище недоступно - вид просто не запомнится */ }
  };

  const patch = (key: string, p: Partial<Cell>) => setDraft(d => ({ ...d, [key]: { ...d[key], ...p } }));
  const resetCell = (key: string) => setDraft(d => ({ ...d, [key]: base[key] }));

  const isDirty = (key: string) => {
    const a = draft[key]; const b = base[key];
    return a.mode !== b.mode || (a.mode !== 'off' && (Number(a.value) !== Number(b.value) || a.period !== b.period));
  };

  const dirtyKeys = Object.keys(draft).filter(isDirty);
  const hasError = catalog.rules.some(def => KIND_ORDER.some(kind => {
    const key = keyOf(def.code, kind);
    return isDirty(key) && cellError(def, draft[key]);
  }));

  const save = async () => {
    const items: NetworkItem[] = [];
    catalog.rules.forEach(def => KIND_ORDER.forEach(kind => {
      const key = keyOf(def.code, kind);
      if (!isDirty(key)) return;
      const c = draft[key];
      const rec = network.find(r => r.rule_code === def.code && r.doc_kind === kind);
      items.push({
        rule_code: def.code, doc_kind: kind, mode: c.mode,
        value: c.mode === 'off' ? (rec ? rec.value : def.defaultValue) : Number(String(c.value).replace(',', '.')),
        period: def.periodic ? c.period : null,
        note: rec?.note ?? null
      });
    }));
    if (!items.length) return;
    setSaving(true);
    try {
      await rulesApi.saveNetwork(items);
      notify.success(`Правила сети сохранены (изменено: ${items.length})`);
      onSaved();
    } catch (err: any) {
      notify.error(apiError(err, 'Не удалось сохранить правила'));
    } finally {
      setSaving(false);
    }
  };

  const modes = catalog.modes.map(m => m.id);
  const periods = catalog.periods.map(p => p.id);

  const viewToggle = (
    <Segmented<View>
      small value={view} onChange={changeView}
      options={[{ value: 'matrix', label: 'Матрица', hint: 'Все правила на одном экране' }, { value: 'list', label: 'Список', hint: 'По одному правилу в блоке' }]}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ px: 2, py: 1.25, borderRadius: '8px', bgcolor: '#fff', border: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: 13, lineHeight: 1.55, color: MUTED }}>
          Значения действуют на всю сеть и задаются отдельно для каждого вида документа. Для отдельных аптек и направлений - вкладка «Исключения».
          Режим «Наблюдение» ничего не блокирует: нарушения только записываются в журнал - так можно безопасно проверить правило перед включением.
        </Typography>
      </Box>

      {view === 'matrix' ? (
        <RuleMatrix
          rules={catalog.rules} modes={modes} periods={periods} draft={draft} keyOf={keyOf} isDirty={isDirty} errorOf={cellError}
          canEdit={canEditNetwork} saving={saving} onPatch={patch} onReset={resetCell} toolbarRight={viewToggle}
          note={catalog.slotFreeStatuses.length > 0
            ? `В лимите направления документы считаются по дате создания; слот не занимают статусы: ${catalog.slotFreeStatuses.map(s => `${s.id} «${s.name}»`).join(', ')}.`
            : undefined}
        />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{viewToggle}</Box>
      )}

      {view === 'list' && catalog.rules.map(def => (
        <Panel key={def.code}>
          <PanelHeader title={def.name} subtitle={def.description} />

          <Box>
            {KIND_ORDER.map((kind, idx) => {
              const key = keyOf(def.code, kind);
              const c = draft[key];
              if (!c) return null;
              const dirty = isDirty(key);
              const err = dirty ? cellError(def, c) : null;
              const off = c.mode === 'off';
              return (
                <Box
                  key={key}
                  sx={{
                    display: 'grid', alignItems: 'start', columnGap: 2.5, rowGap: 1, px: 2.5, py: 1.25,
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(170px, 1fr) auto 150px 215px' },
                    borderTop: idx ? '1px solid #f1f5f9' : 'none', position: 'relative',
                    bgcolor: dirty ? PRIMARY_SOFT : 'transparent', transition: 'background-color .2s',
                    '&::before': {
                      content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: PRIMARY,
                      opacity: dirty ? 1 : 0, transition: 'opacity .2s'
                    }
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: DARK, fontSize: 14, lineHeight: '40px' }}>{KIND_LABELS[kind]}</Typography>

                  <Tooltip title={canEditNetwork ? '' : PERM_HINTS_RULES.networkReadOnly} arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                      {canEditNetwork ? (
                        <Segmented
                          value={c.mode} onChange={v => patch(key, { mode: v })} disabled={saving}
                          options={modeOptions(modes, MODE_LABELS, MODE_HINTS)}
                        />
                      ) : <ModeChip mode={c.mode} />}
                    </Box>
                  </Tooltip>

                  <>
                    <TextField
                      size="small" type="number" value={off ? '' : c.value} placeholder={off ? '-' : undefined}
                      onChange={e => patch(key, { value: e.target.value })}
                      disabled={off || !canEditNetwork || saving} error={Boolean(err)}
                      helperText={err || (def.periodic && !off && Number(c.value) === 0 ? 'Направление закрыто' : ' ')}
                      FormHelperTextProps={{ sx: { mx: 0.5, mt: 0.25, minHeight: 16, fontSize: 11.5, color: err ? undefined : MUTED } }}
                      inputProps={{ min: minValue(def), step: def.integer ? 1 : 100 }}
                      InputProps={{ endAdornment: <InputAdornment position="end"><Box component="span" sx={{ fontSize: 12.5, color: MUTED }}>{def.unit}</Box></InputAdornment> }}
                      sx={{ width: 150, ...FIELD_SX, '& input': { fontSize: 14, fontWeight: 600, py: 1 } }}
                    />
                    {def.periodic && (
                      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                        <Segmented
                          small value={c.period} onChange={v => patch(key, { period: v })} disabled={off || !canEditNetwork || saving}
                          options={periods.map(p => ({ value: p, label: PERIOD_LABELS[p] }))}
                        />
                      </Box>
                    )}
                  </>
                </Box>
              );
            })}
          </Box>

          {def.code === 'direction_limit' && catalog.slotFreeStatuses.length > 0 && (
            <Box sx={{ px: 2.5, py: 1.25, bgcolor: SOFT, borderTop: `1px solid ${BORDER}` }}>
              <Typography sx={{ fontSize: 12.5, color: MUTED, lineHeight: 1.5 }}>
                В лимите направления документы считаются по дате создания; слот не занимают статусы: {catalog.slotFreeStatuses.map(s => `${s.id} «${s.name}»`).join(', ')}.
              </Typography>
            </Box>
          )}
        </Panel>
      ))}

      {dirtyKeys.length > 0 && (
        <Box
          sx={{
            position: 'sticky', bottom: 4, zIndex: 2, display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25,
            bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: FLOAT_SHADOW
          }}
        >
          <Typography sx={{ fontSize: 13.5, color: DARK, fontWeight: 600, mr: 'auto' }}>Несохранённых изменений: {dirtyKeys.length}</Typography>
          <Button disabled={saving} onClick={() => setDraft(base)} sx={GHOST_BTN_SX}>Отменить</Button>
          <Tooltip title={canEditNetwork ? '' : PERM_HINTS_RULES.networkReadOnly}>
            <span>
              <Button
                variant="contained" disableElevation onClick={save} sx={PRIMARY_BTN_SX}
                disabled={!canEditNetwork || hasError || saving}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                Сохранить
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default NetworkRulesTab;
